import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { formatNumber } from '../../lib/format';
import { Skeleton } from '../Skeleton';

/**
 * BARRA DE FLUXO — o destino de quem entrou, numa linha (G2, 07/10/2026).
 *
 * "Entraram 147 · Ficaram 108 · Saíram 31 · Você removeu 8". Os números chegam PRONTOS do
 * servidor e a partição fecha por construção (`entraram = ficaram + saíram + removidos +
 * semCausa`, ver `metricas.md` §4.1) — a barra só desenha a proporção de cada um.
 *
 * ### As cores (DESIGN.md §Colors: cor fixa por categoria, divergente âmbar↔azul)
 *
 * - **ficaram** em `chart-3` (azul): é o que se quer;
 * - **saíram** (por conta própria) em `chart-2` (âmbar): é a evasão — nunca o par
 *   vermelho↔verde lado a lado num gráfico;
 * - **você removeu** em `chart-8`, cinza: limpeza do admin não é culpa da campanha;
 * - **sem causa** em `chart-7`, neutro claro: não sabemos a causa (invariante 12). A hachura
 *   fica reservada para "sem medição" (o trilho inteiro quando `entraram = null`).
 *
 * Cor nunca está sozinha: a legenda escreve o rótulo e o número de cada pedaço (2 canais).
 *
 * ### Estados
 *
 * - `entraram = 0` → trilho vazio + "Ninguém entrou" (é ZERO medido, não ausência);
 * - `entraram = null` → trilho hachurado + "Sem medição" (não sabemos);
 * - `loading` → skeleton no formato da barra.
 */
export interface FlowBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** `null` = sem medição no período. */
  entraram: number | null;
  ficaram: number;
  sairam: number;
  removidos: number;
  semCausa?: number;
  loading?: boolean;
  /** Barra mais baixa e legenda menor (card de campanha). */
  compact?: boolean;
  /** Para leitor de tela: de QUE período é o fluxo ("hoje", "nos últimos 7 dias"). */
  periodLabel?: string;
}

type Pedaco = { chave: string; rotulo: string; valor: number; classe: string; ponto: string };

export function FlowBar({
  entraram,
  ficaram,
  sairam,
  removidos,
  semCausa = 0,
  loading = false,
  compact = false,
  periodLabel,
  className,
  ...props
}: FlowBarProps) {
  const altura = compact ? 'h-2' : 'h-3';

  if (loading) {
    return (
      <div
        role="status"
        aria-busy="true"
        className={cn('flex flex-col gap-2', className)}
        {...props}
      >
        <span className="sr-only">Carregando o fluxo de entradas</span>
        <Skeleton className={cn('w-full rounded-pill', altura)} />
        <Skeleton className="h-3 w-3/4" />
      </div>
    );
  }

  if (entraram === null) {
    return (
      <div data-state="no-data" className={cn('flex flex-col gap-1.5', className)} {...props}>
        <div className={cn('bg-hatch w-full rounded-pill', altura)} aria-hidden="true" />
        <p className="text-2xs text-muted-foreground">
          Sem medição{periodLabel ? ` ${periodLabel}` : ''} — não sabemos quantos entraram.
        </p>
      </div>
    );
  }

  const pedacos: Pedaco[] = [
    {
      chave: 'ficaram',
      rotulo: 'Ficaram',
      valor: ficaram,
      classe: 'bg-chart-3',
      ponto: 'bg-chart-3',
    },
    {
      chave: 'sairam',
      rotulo: 'Saíram',
      valor: sairam,
      classe: 'bg-chart-2',
      ponto: 'bg-chart-2',
    },
    {
      chave: 'removidos',
      rotulo: 'Você removeu',
      valor: removidos,
      classe: 'bg-chart-8',
      ponto: 'bg-chart-8',
    },
    {
      chave: 'semCausa',
      rotulo: 'Sem causa',
      valor: semCausa,
      classe: 'bg-chart-7',
      ponto: 'bg-chart-7',
    },
  ];
  const visiveis = pedacos.filter(
    (p) => p.valor > 0 || p.chave === 'ficaram' || p.chave === 'sairam',
  );

  const resumo =
    entraram === 0
      ? `Ninguém entrou${periodLabel ? ` ${periodLabel}` : ''}`
      : `Entraram ${formatNumber(entraram)}${periodLabel ? ` ${periodLabel}` : ''}: ${visiveis
          .map((p) => `${p.rotulo.toLowerCase()} ${formatNumber(p.valor)}`)
          .join(', ')}`;

  return (
    <div
      data-state={entraram === 0 ? 'zero' : 'data'}
      className={cn('flex flex-col gap-1.5', className)}
      {...props}
    >
      <div
        role="img"
        aria-label={resumo}
        className={cn('flex w-full overflow-hidden rounded-pill bg-muted', altura)}
      >
        {entraram > 0
          ? pedacos.map((p) =>
              p.valor > 0 ? (
                <span
                  key={p.chave}
                  data-segment={p.chave}
                  className={cn('h-full', p.classe)}
                  style={{ width: `${(p.valor / entraram) * 100}%` }}
                />
              ) : null,
            )
          : null}
      </div>
      <p
        className={cn(
          'nums flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted-foreground',
          compact ? 'text-2xs' : 'text-xs',
        )}
        aria-hidden="true"
      >
        {entraram === 0 ? (
          <span>Ninguém entrou{periodLabel ? ` ${periodLabel}` : ''}</span>
        ) : (
          <>
            <span>
              Entraram{' '}
              <strong className="font-semibold text-foreground">{formatNumber(entraram)}</strong>
            </span>
            {visiveis.map((p) => (
              <span key={p.chave} className="inline-flex items-center gap-1">
                <span className={cn('size-2 shrink-0 rounded-pill', p.ponto)} />
                {p.rotulo}{' '}
                <strong className="font-semibold text-foreground">{formatNumber(p.valor)}</strong>
              </span>
            ))}
          </>
        )}
      </p>
    </div>
  );
}
